import { Component } from '@/core/Component';
import { $orNull } from '@/utils/dom';
import type { Language } from '@/types';
import { getTranslation } from '@/utils/translations';
import { debounce, validateEmail, validatePhone } from '@/utils/helpers';
import EmailService from '@/services/EmailService';

interface ContactFields {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
}

interface ValidationErrors {
  [key: string]: string | null;
}

class ContactForm extends Component {
  private form: HTMLFormElement | null = null;
  private submitButton: HTMLButtonElement | null = null;
  private currentLanguage: Language = 'fr';
  private validationErrors: ValidationErrors = {};
  private readonly debouncedValidate: (field: string, value: string) => void;
  private emailService: EmailService;

  constructor() {
    super('#contact-form'); // Pass selector to base class
    this.emailService = new EmailService();
    this.debouncedValidate = debounce((...args: unknown[]) => {
      const [fieldName, value] = args as [string, string];
      this.validateField(fieldName, value);
    }, 300);
    this.init(); // Base class will call mount()
  }

  // Override mount method from Component
  protected mount(): void {
    if (!this.element) return;

    this.form = this.element as HTMLFormElement;
    this.submitButton = $orNull<HTMLButtonElement>('button[type="submit"]', this.form);

    this.attachEventListeners();
  }

  // requiresElement is not strictly needed as default is false, but here we require it?
  // Actually, the Component base class checks `this.element`. If it's null, it won't call mount() unless requiresElement() is false.
  // We want to skip if form doesn't exist.
  protected requiresElement(): boolean {
    return true;
  }

  // Remove the old init method
  // private init(): void { ... }

  private attachEventListeners(): void {
    if (!this.form) return;

    // Handle submit
    this.form.addEventListener('submit', this.handleSubmit.bind(this));

    // Validate/clear errors on field interactions
    const inputs = this.form.querySelectorAll<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >('input, textarea, select');

    inputs.forEach(input => {
      input.addEventListener('blur', e => {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        this.debouncedValidate(target.name, String((target as HTMLInputElement).value ?? ''));
      });

      input.addEventListener('input', e => {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        // Clear error on input
        this.clearFieldError(target.name);
      });
    });
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    if (!this.form) return;

    const formData = this.getFormData();
    if (!this.validateForm(formData)) {
      this.showValidationErrors();
      return;
    }

    this.setLoading(true);

    try {
      // Formulaire complet : champs saisis, champ piège et jeton Turnstile.
      const response = await this.submitForm(new FormData(this.form));

      if (response.success) {
        this.showSuccess();
        this.resetForm();
      } else {
        this.showError(
          response.message || getTranslation(this.currentLanguage, 'form.error.submit')
        );
      }
    } catch (error) {
      this.showError(getTranslation(this.currentLanguage, 'form.error.network'));
    } finally {
      this.setLoading(false);
      // Un jeton Turnstile ne sert qu'une fois : on en redemande un pour un éventuel nouvel envoi.
      (window as { turnstile?: { reset: () => void } }).turnstile?.reset();
    }
  }

  private getFormData(): ContactFields {
    if (!this.form) throw new Error('Form not found');

    const formData = new FormData(this.form);
    return {
      name: (formData.get('name') as string) || '',
      email: (formData.get('email') as string) || '',
      phone: (formData.get('phone') as string) || '',
      company: (formData.get('company') as string) || '',
      message: (formData.get('message') as string) || '',
    };
  }

  private validateForm(data: ContactFields): boolean {
    this.validationErrors = {};

    // Required fields
    if (!data.name.trim()) {
      this.validationErrors.name = getTranslation(this.currentLanguage, 'form.error.required');
    }

    if (!data.email.trim()) {
      this.validationErrors.email = getTranslation(this.currentLanguage, 'form.error.required');
    } else if (!validateEmail(data.email)) {
      this.validationErrors.email = getTranslation(this.currentLanguage, 'form.error.email');
    }

    if (!data.message.trim()) {
      this.validationErrors.message = getTranslation(this.currentLanguage, 'form.error.required');
    } else if (data.message.trim().length < 10) {
      this.validationErrors.message = getTranslation(this.currentLanguage, 'form.error.minLength');
    }

    // Optional phone validation
    if (data.phone && !validatePhone(data.phone)) {
      this.validationErrors.phone = getTranslation(this.currentLanguage, 'form.error.phone');
    }

    return Object.keys(this.validationErrors).length === 0;
  }

  private validateField(fieldName: string, value: string): void {
    switch (fieldName) {
      case 'email':
        if (value && !validateEmail(value)) {
          this.showFieldError(fieldName, getTranslation(this.currentLanguage, 'form.error.email'));
        }
        break;
      case 'phone':
        if (value && !validatePhone(value)) {
          this.showFieldError(fieldName, getTranslation(this.currentLanguage, 'form.error.phone'));
        }
        break;
    }
  }

  private async submitForm(data: FormData): Promise<{ success: boolean; message?: string }> {
    try {
      const result = await this.emailService.sendEmail(data, this.currentLanguage);
      return result;
    } catch (error) {
      return {
        success: false,
        message: getTranslation(this.currentLanguage, 'form.error.network'),
      };
    }
  }

  private showValidationErrors(): void {
    Object.entries(this.validationErrors).forEach(([field, message]) => {
      if (message) {
        this.showFieldError(field, message);
      }
    });
  }

  private showFieldError(fieldName: string, message: string): void {
    const field = this.form?.querySelector(`[name="${fieldName}"]`) as HTMLElement;
    if (!field) return;

    // Remove existing error
    this.clearFieldError(fieldName);

    // Add error class
    field.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500');
    field.classList.remove('border-gray-300', 'focus:border-blue-500', 'focus:ring-blue-500');

    // Create error message element
    const errorElement = document.createElement('p');
    errorElement.className = 'mt-1 text-sm text-red-600 field-error';
    errorElement.textContent = message;

    // Insert after field
    field.parentNode?.insertBefore(errorElement, field.nextSibling);
  }

  private clearFieldError(fieldName: string): void {
    const field = this.form?.querySelector(`[name="${fieldName}"]`) as HTMLElement;
    if (!field) return;

    // Remove error classes
    field.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500');
    field.classList.add('border-gray-300', 'focus:border-blue-500', 'focus:ring-blue-500');

    // Remove error message
    const errorElement = field.parentNode?.querySelector('.field-error');
    errorElement?.remove();
  }

  private showSuccess(): void {
    this.showNotification(
      getTranslation(this.currentLanguage, 'form.success.title'),
      getTranslation(this.currentLanguage, 'form.success.message'),
      'success'
    );
  }

  private showError(message: string): void {
    this.showNotification(
      getTranslation(this.currentLanguage, 'form.error.title'),
      message,
      'error'
    );
  }

  private showNotification(title: string, message: string, type: 'success' | 'error'): void {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `
      fixed top-4 right-4 z-50 max-w-sm p-4 rounded-lg shadow-lg
      ${type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}
      transform transition-all duration-300 translate-x-full
    `;

    notification.innerHTML = `
      <div class="flex items-center">
        <div class="flex-1">
          <h4 class="font-semibold">${title}</h4>
          <p class="text-sm opacity-90">${message}</p>
        </div>
        <button class="ml-2 text-white hover:text-gray-200">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
      </div>
    `;

    // Add to DOM
    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);

    // Add close functionality
    const closeBtn = notification.querySelector('button');
    closeBtn?.addEventListener('click', () => this.closeNotification(notification));

    // Auto remove after 5 seconds
    setTimeout(() => this.closeNotification(notification), 5000);
  }

  private closeNotification(notification: HTMLElement): void {
    notification.classList.add('translate-x-full');
    setTimeout(() => notification.remove(), 300);
  }

  private resetForm(): void {
    if (!this.form) return;

    this.form.reset();

    // Clear all errors
    const errorElements = this.form.querySelectorAll('.field-error');
    errorElements.forEach(el => el.remove());

    // Reset field styles
    const fields = this.form.querySelectorAll('input, textarea, select');
    fields.forEach(field => {
      field.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500');
      field.classList.add('border-gray-300', 'focus:border-blue-500', 'focus:ring-blue-500');
    });
  }

  private setLoading(loading: boolean): void {
    if (!this.submitButton) return;

    if (loading) {
      this.submitButton.disabled = true;
      this.submitButton.innerHTML = `
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        ${getTranslation(this.currentLanguage, 'form.sending')}
      `;
    } else {
      this.submitButton.disabled = false;
      this.submitButton.textContent = getTranslation(this.currentLanguage, 'form.submit');
    }
  }

  public updateLanguage(language: Language): void {
    this.currentLanguage = language;
    // Update button text if not loading
    if (this.submitButton && !this.submitButton.disabled) {
      this.submitButton.textContent = getTranslation(language, 'form.submit');
    }
  }
}

export default ContactForm;
